import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {FC, useCallback, useMemo} from 'react';

import {captureError} from '../integrations';
import * as PanelViewTypes from '../state/views/panel/types';
import {getDatadogRum} from '../util/datadog/datadogRum';
import {BasePanelProps} from '../util/panelProps';
import {
  getPanelSpec,
  Panel,
  PanelConfig,
  PanelExportRef,
} from '../util/panelTypes';
import {ErrorBoundary} from './ErrorBoundary';
import {useSingleRunModeContext} from './MultiRunWorkspace/SingleRunMode';
import {usePanelSharedProps} from './Panel/hooks/usePanelSharedProps';
import {usePanelContext} from './Panel/PanelContextProvider';
import {useStablePanelId} from './Panel/PanelIDContext';
import {default as PanelRunsLinePlot} from './PanelRunsLinePlot/ComponentWrapper';
import {InstrumentedCenteredWaveLoader as Loader} from './utility/InstrumentedLoader';
import {usePanelWithInheritedSettings} from './WorkspaceDrawer/Settings/hooks/usePanelInheritedSettings';

type PanelCompProps = Parameters<typeof PanelComp>[0];
type PanelCompReduxProps = Omit<
  PanelCompProps,
  | 'panel'
  | 'updateConfig'
  | 'data'
  | 'loading'
  | 'runsDataQuery'
  | 'query'
  | 'pageQuery'
  | 'customRunColors'
  | 'customRunNames'
> & {
  panelRef: PanelViewTypes.Ref;
  panelExportRef?: React.MutableRefObject<PanelExportRef | undefined>;
};

const PanelComp = (
  props: BasePanelProps & {
    panel: Panel;
    panelExportRef?: React.MutableRefObject<PanelExportRef | undefined>;
    updateConfig(
      newConfig: PanelConfig,
      options?: {isUndoable?: boolean}
    ): void;
  }
) => {
  const {panel, updateConfig, ...passThroughProps} = props;
  const {isSingleRun} = useSingleRunModeContext();

  const {
    Component: PanelComponent,
    transformConfig,
    transformConfigUpdate,
  } = getPanelSpec(panel.viewType);

  const configTransformed = useMemo(
    () => transformConfig?.(panel.config as any, {isSingleRun}) ?? panel.config,
    [panel.config, transformConfig, isSingleRun]
  );

  const transformAndUpdateConfig = useCallback(
    (newConfig: PanelConfig, options?: {isUndoable?: boolean}) => {
      const newConfigTransformed =
        transformConfigUpdate != null
          ? transformConfigUpdate(newConfig as any, {isSingleRun})
          : newConfig;
      updateConfig(newConfigTransformed, options);
    },
    [updateConfig, transformConfigUpdate, isSingleRun]
  );

  /**
   * Typically we lazy load the panel components, but this creates a double loading state for full fidelity runs line plots because each panel has to mount in order to dispatch its queries (which it now owns instead of the run selector). Directly importing this component and mounting it without suspense gets rid of the redundant loading state and saves users a quick blink and some layout shift in the underlying loader animation.
   */
  if (panel.viewType === 'Run History Line Plot') {
    return (
      <PanelRunsLinePlot
        {...passThroughProps}
        config={configTransformed as any}
        updateConfig={
          transformAndUpdateConfig as (
            config: any,
            options?: {isUndoable?: boolean}
          ) => void
        }
      />
    );
  }

  return (
    <React.Suspense
      fallback={
        <div className="loader-wrapper">
          <Loader name={'panel-comp-suspense-loader'} size="huge" />
        </div>
      }>
      <PanelComponent
        {...passThroughProps}
        // Here we know that s.transformQuery can be called with panel.config, because
        // spec.type === panel.viewType. However, there's no way to get typescript
        // to narrow a union to an invidual member, so we just cast to any.
        config={configTransformed as any}
        updateConfig={
          transformAndUpdateConfig as (
            config: any,
            options?: {isUndoable?: boolean}
          ) => void
        }
      />
    </React.Suspense>
  );
};

export const PanelCompRedux: FC<PanelCompReduxProps> = props => {
  const {panelExportRef, panelRef} = props;
  const {inheritedSettings} = usePanelContext();
  const panel = usePanelWithInheritedSettings(panelRef, inheritedSettings);
  const sharedProps = usePanelSharedProps(panelRef);

  const logError = React.useCallback(
    (error: Error) => {
      // Send to Segment (view in Mixpanel)
      window.analytics?.track('Panel Error', {
        viewType: panel.viewType,
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack,
      });
      // Send to Sentry
      captureError(error, 'Panel ErrorBoundary error');
      // Send to Datadog RUM
      const dd_rum = getDatadogRum();
      dd_rum.addError(error, {
        message: 'Panel ErrorBoundary error',
        panelViewType: panel.viewType,
      });
    },
    [panel.viewType]
  );
  const stablePanelId = useStablePanelId();

  return (
    <ErrorBoundary
      onError={logError}
      renderError={() => renderPanelError(stablePanelId, panel.viewType)}>
      <PanelComp
        {...props}
        {...sharedProps}
        panel={panel}
        panelExportRef={panelExportRef}
      />
    </ErrorBoundary>
  );
};

function renderPanelError(panelID: string, panelViewType: string) {
  return (
    <div style={{padding: 40}}>
      <PanelError
        panelID={panelID}
        panelViewType={panelViewType}
        message={
          <div>
            Oops, something went wrong. If this keeps happening, message
            support@wandb.com with a link to this page
          </div>
        }
        logErrorMessage="Oops, something went wrong. If this keeps happening, message support@wandb.com with a link to this page"
      />
    </div>
  );
}
